import React, { useState, useEffect } from 'react';

// ─── Install prompt (v83) ────────────────────────────────────────────────
// Shown once per user, the first time a logged-in user sees the app.
// Platform-aware copy:
//   • Android/Chrome: native install button (wired to beforeinstallprompt)
//   • iOS Safari:     instruction card (Apple doesn't expose an install API,
//                     so we tell users "tap share, then 'Add to Home Screen'")
//   • Already installed (running in standalone mode): never shown
// Stored dismissal: localStorage key 'gt_install_dismissed_v1' = 'true'.
//
// Soft, bottom-anchored card. Not a modal — doesn't block use of the app.

const DISMISS_KEY = 'gt_install_dismissed_v1';

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true; // iOS Safari sets this when installed

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isAndroid = () => /android/i.test(navigator.userAgent);

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Hard gates: already installed, or previously dismissed.
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY) === 'true') return;

    // Show only on mobile platforms — desktop "install" PWAs exist but the
    // adoption story we care about is phone-on-home-screen.
    if (!isIOS() && !isAndroid()) return;

    // Capture the Android install event so we can fire the native prompt
    // from our own button. iOS never fires this — handled by instructions.
    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // Show after a short delay so we don't slap users in the face on load.
    const t = setTimeout(() => setVisible(true), 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  };

  const triggerAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    // Whether they accept or decline, don't show again.
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const ios = isIOS();

  return (
    <div style={{
      position: 'fixed',
      // v85b.1: respect iOS home-bar safe area (16px gap PLUS the inset).
      bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
      left: 16, right: 16,
      maxWidth: 420, margin: '0 auto', zIndex: 1000,
      background: '#fff', border: '1px solid #e8e4d8',
      borderRadius: 12, padding: 16,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      fontFamily: "'DM Sans', Arial, sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <img
          src="/apple-touch-icon.png"
          alt=""
          width={44}
          height={44}
          style={{ borderRadius: 10, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#0d2b1a', marginBottom: 4 }}>
            Add Greentime to your home screen
          </div>
          <div style={{ fontSize: 12.5, color: '#5a5a4a', lineHeight: 1.45 }}>
            {ios ? (
              <>
                Tap the <strong>share</strong> button at the bottom of Safari, then
                scroll and tap <strong>“Add to Home Screen.”</strong>
              </>
            ) : (
              <>One tap and Greentime launches like an app, full-screen, from your home screen.</>
            )}
          </div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={dismiss}
              style={{
                background: 'transparent', border: 'none', color: '#7a7a6a',
                fontSize: 13, padding: '6px 10px', cursor: 'pointer',
              }}
            >
              Not now
            </button>
            {!ios && deferredPrompt && (
              <button
                onClick={triggerAndroidInstall}
                style={{
                  background: '#7ec99a', color: '#0d2b1a', border: 'none',
                  borderRadius: 8, padding: '8px 14px', fontSize: 13,
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                Install
              </button>
            )}
            {ios && (
              <button
                onClick={dismiss}
                style={{
                  background: '#7ec99a', color: '#0d2b1a', border: 'none',
                  borderRadius: 8, padding: '8px 14px', fontSize: 13,
                  fontWeight: 500, cursor: 'pointer',
                }}
              >
                Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
